import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Linkedin, ArrowUpRight } from "lucide-react";
import { useLang } from "../contexts/LanguageContext";
import { api } from "../lib/api";
import Reveal from "../components/Reveal";
import MagneticButton from "../components/MagneticButton";

const WORKSPACE_IMG = "https://static.prod-images.emergentagent.com/jobs/d8466647-e52b-4472-8605-b6bae3cfd7a8/images/c2ed4898cbcf4a643b5d097d965dbe60def4e4130884a944ff2f8a98c25e0644.png";

const About = () => {
  const { lang, t } = useLang();
  const [team, setTeam] = useState([]);

  useEffect(() => {
    api.get("/team").then(({ data }) => setTeam(data)).catch(() => {});
  }, []);

  return (
    <div data-testid="about-page">
      {/* HERO */}
      <section className="section-pad" style={{ paddingTop: "clamp(160px, 18vw, 220px)" }}>
        <div className="font-mono line-deco" style={{ fontSize: "0.75rem", letterSpacing: "0.2em", color: "var(--gold)", textTransform: "uppercase", marginBottom: 32 }}>
          {t.about.label}
        </div>
        <h1 className="font-display" style={{ fontSize: "clamp(3rem, 9vw, 8rem)", fontWeight: 300, lineHeight: 0.95, color: "var(--warm-off-white)", letterSpacing: "-0.02em" }}>
          <span style={{ fontStyle: "italic", color: "var(--gold-light)" }}>{t.about.title.split(" ")[0]}</span> {t.about.title.split(" ").slice(1).join(" ")}
        </h1>
        <p className="font-body" style={{ color: "rgba(248,244,240,0.65)", lineHeight: 1.7, marginTop: 24, fontSize: "1.1rem", maxWidth: 640 }}>
          {t.about.intro}
        </p>
      </section>

      {/* STORY split */}
      <section className="section-pad" style={{ paddingTop: 0 }}>
        <div className="grid" style={{ gridTemplateColumns: "1fr 1fr", gap: 64, alignItems: "center" }}>
          <Reveal>
            <div className="font-mono line-deco" style={{ fontSize: "0.75rem", letterSpacing: "0.2em", color: "var(--gold)", textTransform: "uppercase", marginBottom: 24 }}>
              {t.about.storyTitle}
            </div>
            <h2 className="font-display" style={{ fontSize: "clamp(2.25rem, 4vw, 3.75rem)", fontWeight: 300, lineHeight: 1.1, color: "var(--warm-off-white)" }}>
              {lang === "tr" ? "Hikâyemiz büyük bir vizyonla başladı." : "Our story began with a bold vision."}
            </h2>
            <p style={{ color: "rgba(248,244,240,0.72)", lineHeight: 1.85, marginTop: 24, fontSize: "1.05rem" }}>
              {t.about.story}
            </p>
          </Reveal>
          <Reveal delay={150}>
            <div style={{ position: "relative", aspectRatio: "4/5", overflow: "hidden" }} className="corner-deco">
              <img src={WORKSPACE_IMG} alt="Larcraft workspace" style={{ width: "100%", height: "100%", objectFit: "cover", filter: "brightness(0.75)" }} />
              <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, transparent 50%, rgba(139,32,53,0.4) 100%)" }} />
            </div>
          </Reveal>
        </div>
      </section>

      {/* VALUES */}
      <section className="section-pad" style={{ background: "linear-gradient(180deg, var(--rich-black) 0%, var(--rich-black-light) 100%)" }}>
        <div className="font-mono line-deco" style={{ fontSize: "0.75rem", letterSpacing: "0.2em", color: "var(--gold)", textTransform: "uppercase", marginBottom: 32 }}>
          {t.about.valuesTitle}
        </div>
        <h2 className="font-display" style={{ fontSize: "clamp(2.25rem, 4vw, 3.75rem)", fontWeight: 300, color: "var(--warm-off-white)", marginBottom: 64 }}>
          {lang === "tr" ? "Bizi biz yapan altı söz." : "Six words that make us, us."}
        </h2>
        <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 24 }}>
          {t.about.values.map((v, i) => (
            <Reveal key={i} delay={i * 80}>
              <div style={{ padding: 32, background: "rgba(17,17,17,0.5)", border: "1px solid rgba(139,32,53,0.25)" }}>
                <div className="font-mono" style={{ fontSize: "0.7rem", letterSpacing: "0.2em", color: "var(--gold)", textTransform: "uppercase" }}>0{i + 1}</div>
                <h3 className="font-display" style={{ fontSize: "1.75rem", fontWeight: 400, color: "var(--warm-off-white)", marginTop: 16 }}>{v.t}</h3>
                <p style={{ color: "rgba(248,244,240,0.65)", lineHeight: 1.7, marginTop: 12 }}>{v.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* TIMELINE */}
      <section className="section-pad">
        <div className="font-mono line-deco" style={{ fontSize: "0.75rem", letterSpacing: "0.2em", color: "var(--gold)", textTransform: "uppercase", marginBottom: 32 }}>
          {t.about.timelineTitle}
        </div>
        <div style={{ position: "relative", paddingLeft: 32, borderLeft: "1px solid rgba(201,151,58,0.4)", marginTop: 48 }}>
          {t.about.timeline.map((m, i) => (
            <Reveal key={i} delay={i * 80}>
              <div style={{ position: "relative", paddingBottom: 48 }}>
                <div style={{ position: "absolute", left: -39, top: 6, width: 14, height: 14, background: "var(--bordeaux)", border: "2px solid var(--gold)" }} />
                <div className="font-mono" style={{ fontSize: "1rem", color: "var(--gold)", letterSpacing: "0.15em" }}>{m.y}</div>
                <p className="font-display" style={{ fontSize: "1.75rem", color: "var(--warm-off-white)", marginTop: 8, fontWeight: 300 }}>{m.t}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* TEAM */}
      <section className="section-pad" style={{ background: "linear-gradient(180deg, var(--rich-black-light) 0%, var(--rich-black) 100%)" }}>
        <div className="font-mono line-deco" style={{ fontSize: "0.75rem", letterSpacing: "0.2em", color: "var(--gold)", textTransform: "uppercase", marginBottom: 32 }}>
          {t.about.teamTitle}
        </div>
        <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 32, marginTop: 48 }}>
          {team.map((m, i) => (
            <Reveal key={m.id} delay={i * 100}>
              <div data-testid={`team-card-${m.id}`} style={{ position: "relative" }}>
                <div style={{ position: "relative", aspectRatio: "3/4", overflow: "hidden", background: "var(--rich-black-light)" }}>
                  <img src={m.photo_url} alt={m.name} loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover", filter: "grayscale(0.4) brightness(0.85)", transition: "filter 0.6s var(--ease-cinema)" }} onMouseEnter={(e) => (e.currentTarget.style.filter = "grayscale(0) brightness(1)")} onMouseLeave={(e) => (e.currentTarget.style.filter = "grayscale(0.4) brightness(0.85)")} />
                  <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, transparent 60%, rgba(10,10,10,0.85) 100%)" }} />
                </div>
                <h3 className="font-display" style={{ fontSize: "1.6rem", fontWeight: 400, color: "var(--warm-off-white)", marginTop: 16 }}>{m.name}</h3>
                <div className="font-mono" style={{ fontSize: "0.75rem", letterSpacing: "0.18em", color: "var(--gold)", textTransform: "uppercase", marginTop: 6 }}>{lang === "tr" ? m.role_tr : m.role_en}</div>
                <p style={{ color: "rgba(248,244,240,0.6)", marginTop: 12, lineHeight: 1.6, fontSize: "0.9rem" }}>{lang === "tr" ? m.bio_tr : m.bio_en}</p>
                <div className="flex" style={{ gap: 12, marginTop: 16 }}>
                  {m.linkedin && <a href={m.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" style={{ color: "rgba(248,244,240,0.5)" }}><Linkedin size={16} /></a>}
                  {m.instagram && <a href={m.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" style={{ color: "rgba(248,244,240,0.5)" }}><Instagram size={16} /></a>}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section style={{ background: "linear-gradient(135deg, var(--bordeaux-dark), var(--bordeaux))", padding: "clamp(80px, 12vw, 160px) clamp(24px, 6vw, 96px)" }}>
        <Reveal>
          <h2 className="font-display" style={{ fontSize: "clamp(2.5rem, 6vw, 5.5rem)", fontWeight: 300, color: "var(--warm-off-white)", lineHeight: 1.05, maxWidth: 1100 }}>
            {t.about.ctaTitle}
          </h2>
          <Link to="/contact" style={{ textDecoration: "none", display: "inline-block", marginTop: 48 }}>
            <MagneticButton className="font-mono" style={{ padding: "20px 44px", background: "var(--warm-off-white)", color: "var(--bordeaux-dark)", border: "none", textTransform: "uppercase", letterSpacing: "0.2em", fontSize: "0.75rem", cursor: "pointer" }}>
              {t.about.ctaButton} <ArrowUpRight size={16} style={{ marginLeft: 8 }} />
            </MagneticButton>
          </Link>
        </Reveal>
      </section>
    </div>
  );
};

export default About;
